import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { MapLayer, MapLayerState } from "../../layers/types";

const initialState: MapLayerState = {
  layers: [],
};

const layerSlice = createSlice({
  name: "layers",
  initialState,
  reducers: {
    addLayer(state, action: PayloadAction<MapLayer>) {
      state.layers.push(action.payload);
    },
    removeLayer(state, action: PayloadAction<string | null>) {
      state.layers = state.layers.filter((l) => l.id !== action.payload);
    },

    updateLayer(state, action: PayloadAction<{ id: string; patch: Partial<MapLayer> }>) {
      const { id, patch } = action.payload;
      state.layers = state.layers.map((l) => (l.id === id ? { ...l, ...patch } : l));
    },

    toggleVisible(state, action: PayloadAction<string | null>) {
      state.layers = state.layers.map((l) => (l.id === action.payload ? { ...l, visibility: !l.visibility } : l));
    },

    setOpacity(state, action: PayloadAction<{ id: string; opacity: number }>) {
      const { id, opacity } = action.payload;
      state.layers = state.layers.map((l) => (l.id === id ? { ...l, opacity: Math.max(0, Math.min(1, opacity)) } : l));
    },
    moveLayerUp(state, action: PayloadAction<string | null>) {
      const id = action.payload;
      if (!id) return;
      const idx = state.layers.findIndex((l) => l.id === id);
      if (idx === -1 || idx >= state.layers.length - 1) return;
      // swap with next layer
      const nextIdx = idx + 1;
      [state.layers[idx], state.layers[nextIdx]] = [state.layers[nextIdx], state.layers[idx]];
      // recalc zIndex order
      state.layers.forEach((l, i) => {
        l.zIndex = i;
      });
    },
    moveLayerDown(state, action: PayloadAction<string | null>) {
      const id = action.payload;
      if (!id) return;
      const idx = state.layers.findIndex((l) => l.id === id);
      if (idx <= 0) return;
      const prevIdx = idx - 1;
      [state.layers[idx], state.layers[prevIdx]] = [state.layers[prevIdx], state.layers[idx]];
      state.layers.forEach((l, i) => {
        l.zIndex = i;
      });
    },
    reorderLayers(state, action: PayloadAction<string[]>) {
      const ids = action.payload;
      const map = new Map(state.layers.map((l) => [l.id, l]));
      state.layers = ids.map((id, i) => ({ ...map.get(id)!, zIndex: i })).filter(Boolean);
    },

    clearAllLayers(state) {
      state.layers = [];
    },
  },
});

export const { addLayer, removeLayer, updateLayer, toggleVisible, setOpacity, moveLayerUp, moveLayerDown, reorderLayers, clearAllLayers } = layerSlice.actions;
export default layerSlice.reducer;
